import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Box } from "@mui/material";

import StackedNavigationHeader from "./StackedNavigationHeader";

import { StackablePanel } from "./StackedNavigation.styles";

interface IStackedNavigationContext {
  stack: string[];
  params: Record<string, any>;
  open: (id: string, params?: Record<string, any>) => void;
  goBack: (count?: number) => void;
  clear: () => void;
}

export const StackedNavigationContext = createContext<IStackedNavigationContext>(undefined!);

export const useStackedNavigationContext = (): IStackedNavigationContext => useContext(StackedNavigationContext);

type Screen = {
  id: string;
  name: string;
  component: React.ReactNode;
  useDefaultHeader?: boolean;
};

interface IProps {
  rootComponent: React.ReactNode;
  screens: Screen[];
}

const StackedNavigation: React.FC<IProps> = ({ rootComponent, screens }) => {
  const [stack, setStack] = useState<string[]>([]);
  const [params, setParams] = useState<Record<string, any>>({});

  const open = useCallback(
    (entryId: string, entryParams?: Record<string, any>) => {
      const screenIds = screens.map((screen) => screen.id);
      if (!screenIds.includes(entryId)) {
        throw new Error("Invalid target screen ID");
      }

      setStack((currentStack) => [...currentStack, entryId]);
      if (entryParams) {
        setParams((currentParams) => ({ ...currentParams, [entryId]: entryParams }));
      }
    },
    [screens]
  );
  const popStack = (count = 1) => setStack((currentStack) => currentStack.slice(0, currentStack.length - count));
  const resetStack = () => setStack([]);

  const providerValue = useMemo(() => ({ stack, params, open, goBack: popStack, clear: resetStack }), [open, stack, params]);

  return (
    <StackedNavigationContext.Provider value={providerValue}>
      <Box position="relative" height="100%" overflow="hidden">
        {rootComponent}
        {stack.map((id) => {
          const targetScreenIndex = screens.findIndex((screen) => screen.id === id);
          if (targetScreenIndex === -1) return null;
          const { name, component, useDefaultHeader = true } = screens[targetScreenIndex];

          return (
            <StackablePanel data-testid={`stackable-panel-${id}`} key={id} className="customScrollbars">
              {useDefaultHeader && <StackedNavigationHeader onBackButtonClick={() => popStack(1)} title={name} />}
              {component}
            </StackablePanel>
          );
        })}
      </Box>
    </StackedNavigationContext.Provider>
  );
};

export default StackedNavigation;
